import _ from "lodash";
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import classes from "./VideoPlayerV2.scss";
import ReactPlayerDefault from "react-player";
import ReactPlayerLazy from "react-player/lazy";
import { IconButton, useDSContext } from "@toddle-edu/ds-web";
import {
  CollapseArrowsTwoOutlined,
  ExpandArrowsFourOutlined,
  PauseFilled,
  PlayFilled,
  VolumeHighOutlined,
  VolumeLowOutlined,
} from "@toddle-edu/ds-icons";
import Loading from "UIComponents/Loading";
import moment from "moment";
import classNames from "classnames";

import { isIpad, isIphone, isSafari } from "Utils";
import { allowURLWithCredentials } from "Utils/SecureUrlHelpers";
import ACLStore from "lib/aclStore";

const styles = {
  containerStyle: { width: "100%", height: "100%" },
  iconStyle: { paddingTop: "0" },
};

// Tracks the last-clicked player so spacebar only affects that one and doesn't trigger play/pause on all players rendered in the page
let activePlayerContainer = null;

const VideoPlayerV2 = React.memo(
  ({
    videoUrl,
    isHLSUrl,
    autoPlay,
    getPlayerRef,
    videoPlaybackTime = 0,
    showControls,
    showPlayButton,
    shouldFitVideoCover = false,
    useLazyVideoPlayer = false,
    isolateSpacebarControl = false,
  }) => {
    const ReactPlayer = useLazyVideoPlayer
      ? ReactPlayerLazy
      : ReactPlayerDefault;
    const playerRef = useRef(null);
    const containerRef = useRef(null);
    const [currentDuration, setCurrentDuration] = useState(0);
    const [totalDuration, setTotalDuration] = useState(0);
    const [playStatus, setPlayStatus] = useState(autoPlay);
    const [isMuted, setIsMuted] = useState(false);
    const [isBufferProgress, setBufferProgress] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isControlsVisible, setIsControlsVisible] = useState(false);
    const [showCenterPlay, setShowCenterPlay] = useState(!autoPlay);
    const controlsVisibilityTimer = useRef(null);
    const skipForceHlsConfig = ACLStore.can("FeatureFlag:skipForceHlsConfig");
    const { dsVersion } = useDSContext();
    const controlsVisibilityHandler = useCallback(
      _.debounce(() => {
        if (controlsVisibilityTimer?.current != null) {
          // reset timeout if mouse is still moving
          clearTimeout(controlsVisibilityTimer?.current);
          controlsVisibilityHandler.current = null;
        }
        // Hide controls after 5 seconds
        controlsVisibilityTimer.current = setTimeout(() => {
          setIsControlsVisible(false);
        }, 5000);
        setIsControlsVisible(true);
      }, 100),
      []
    );

    const handleFullScreenFromSystemEvent = useCallback(() => {
      const fullscreenElement =
        document.fullscreenElement /* Standard syntax */ ||
        document.webkitFullscreenElement /* Chrome, Safari and Opera syntax */ ||
        document.mozFullScreenElement /* Firefox syntax */ ||
        document.msFullscreenElement;
      if (!_.isNil(fullscreenElement)) setIsFullScreen(true);
      else setIsFullScreen(false);
    }, []);

    const playOrPauseVideo = state => {
      setPlayStatus(state);
      if (state == true) {
        setTimeout(() => setShowCenterPlay(false), 300);
      } else {
        setShowCenterPlay(true);
      }
    };

    useEffect(() => {
      if (isFullScreen) {
        document.addEventListener("mousemove", controlsVisibilityHandler);
      }
      // Cleanup mouse move listener and timeout
      return () => {
        document.removeEventListener("mousemove", controlsVisibilityHandler);
        if (controlsVisibilityTimer?.current != null) {
          clearTimeout(controlsVisibilityTimer?.current);
          controlsVisibilityTimer.current = null;
        }
      };
    }, [isFullScreen]);

    const getBrowserEvent = () => {
      if (isSafari()) {
        return "webkitfullscreenchange";
      } else {
        return "fullscreenchange";
      }
    };

    useEffect(() => {
      setTimeout(() => {
        if (
          !_.isNil(playerRef?.current) &&
          !_.isNaN(playerRef?.current?.getDuration())
        )
          setTotalDuration(playerRef?.current?.getDuration()); // this will load the total duration of video in the state.
      }, 1000);

      // Used to detect full screen change made by system/custom control
      document.addEventListener(
        getBrowserEvent(),
        handleFullScreenFromSystemEvent
      );

      // Cleanup all listeners
      return () => {
        document.removeEventListener("mousemove", controlsVisibilityHandler);
        document.addEventListener(
          getBrowserEvent(),
          handleFullScreenFromSystemEvent
        );
      };
    }, []);

    const handleKeyPress = useCallback(
      event => {
        if (
          isolateSpacebarControl &&
          activePlayerContainer !== containerRef.current
        )
          return;
        const keyPressed = event?.key;
        const targetElement = event?.target?.tagName;
        if (
          _.includes(["TEXTAREA", "INPUT"], targetElement) ||
          event?.target?.isContentEditable
        )
          return;
        switch (keyPressed) {
          case " ": // spacebar event key code
          case "Spacebar":
            event?.preventDefault(); // prevent page scroll on spacebar press
            playOrPauseVideo(!playStatus);
            break;
        }
      },
      [playStatus, isolateSpacebarControl]
    );

    useEffect(() => {
      document.addEventListener("keydown", handleKeyPress);

      return () => {
        document.removeEventListener("keydown", handleKeyPress);
      };
    }, [handleKeyPress]);

    useEffect(() => {
      if (!isolateSpacebarControl) return;
      const container = containerRef.current;
      return () => {
        if (activePlayerContainer === container) {
          activePlayerContainer = null;
        }
      };
    }, [isolateSpacebarControl]);

    const [isReady, setIsReady] = useState(false);

    const onPlayerReady = e => {
      if (!isReady) {
        const duration = e?.getDuration?.();
        if (duration === Infinity) {
          e.seekTo(1e101, "seconds");
        } else if (!_.isNil(playerRef?.current)) {
          if (_.isNumber(duration) && duration !== Infinity) {
            setTotalDuration(duration);
          }
          playerRef?.current?.seekTo(videoPlaybackTime, "seconds");
          setIsReady(true);
        }
      }
    };

    const getRef = ref => {
      playerRef.current = ref;
      if (getPlayerRef) {
        getPlayerRef(ref);
      }
    };
    const onSeekbarChange = e => {
      const time = e?.target?.value;
      if (!_.isNull(playerRef?.current)) {
        playerRef?.current?.seekTo(time, "seconds");
        if (totalDuration == 0)
          setTotalDuration(playerRef?.current?.getDuration() ?? 0); // If user seeks to particular time directly without starting the media playback
      }
    };

    const toggleMute = e => {
      e?.stopPropagation();
      setIsMuted(!isMuted);
      e?.currentTarget?.blur();
    };
    const toggleFullScreen = e => {
      try {
        e?.stopPropagation();
        if (!_.isNull(containerRef?.current)) {
          if (isFullScreen) {
            if (_.isFunction(_.get(document, "exitFullscreen"))) {
              document.exitFullscreen?.();
            } else if (
              isSafari() &&
              _.isFunction(_.get(document, "webkitExitFullscreen"))
            ) {
              document.webkitExitFullscreen?.();
            } else if (_.isFunction(_.get(document, "msExitFullscreen"))) {
              document.msExitFullscreen?.();
            }
          } else {
            if (
              _.isFunction(_.get(containerRef?.current, "requestFullscreen"))
            ) {
              const videContainerElement = containerRef?.current;
              const requestFullscreen = videContainerElement.requestFullscreen;

              if (
                requestFullscreen &&
                _.isFunction(_.get(videContainerElement, "requestFullscreen"))
              ) {
                videContainerElement?.requestFullscreen?.();
              }
            } else if (
              isSafari() &&
              _.isFunction(
                _.get(containerRef?.current, "webkitRequestFullscreen")
              )
            ) {
              containerRef?.current.webkitRequestFullscreen?.();
            } else if (
              _.isFunction(_.get(containerRef?.current, "msRequestFullscreen"))
            ) {
              containerRef?.current?.msRequestFullscreen?.();
            } else if (document.fullscreenElement == null)
              containerRef?.current?.requestFullscreen?.();
          }
        }
        e?.currentTarget?.blur?.();
      } catch (error) {}
    };

    const togglePlayback = e => {
      e?.stopPropagation();
      playOrPauseVideo(!playStatus);
      e?.currentTarget?.blur();
    };

    const onProgressChange = data => {
      if (isBufferProgress) setBufferProgress(false);
      setCurrentDuration(_.get(data, "playedSeconds", 0));
    };

    const getControlsVisibility = () => {
      if (isFullScreen)
        return { visibility: isControlsVisible ? "visible" : "hidden" }; // visible for 5 seconds, then hide ( by useEffect )
      return {};
    };
    const containerClass = classNames({
      [classes.container]: true,
      [classes.coverFitVideoContainer]: shouldFitVideoCover,
    });

    const PlayerConfig = useMemo(() => {
      return {
        ...(!(skipForceHlsConfig && (isIphone() || isIpad()))
          ? { forceHLS: isHLSUrl }
          : {}),
        hlsOptions: {
          xhrSetup: function (xhr, url) {
            xhr.withCredentials = allowURLWithCredentials(url);
          },
        },
      };
    }, [isHLSUrl]);

    const onPlay = () => {
      playOrPauseVideo(true);
    };

    const onPause = () => {
      playOrPauseVideo(false);
    };

    const onBuffer = () => {
      setBufferProgress(true);
    };

    const onBufferEnd = () => {
      setBufferProgress(false);
    };

    const onDuration = duration => {
      if (_.isNumber(duration) && duration !== Infinity) {
        setTotalDuration(duration);
      }
    };

    // * Getting video duration in HH:mm:ss or mm:ss format for a valid value
    const durationDetails = useMemo(() => {
      const currentDurationTime = moment
        .duration(currentDuration, "seconds")
        .format(currentDuration >= 3600 ? "HH:mm:ss" : "mm:ss", {
          trim: false,
        });

      const totalDurationTime = moment
        .duration(totalDuration, "seconds")
        .format(totalDuration >= 3600 ? "HH:mm:ss" : "mm:ss", {
          trim: false,
        });

      if (
        _.includes(currentDurationTime, "NaN") ||
        _.includes(totalDurationTime, "NaN")
      ) {
        // * When the video is not loaded or duration is not available
        return "00:00 / 00:00";
      }

      return `${currentDurationTime} / ${totalDurationTime}`;
    }, [currentDuration, totalDuration]);

    const setActivePlayer = () => {
      if (isolateSpacebarControl) {
        activePlayerContainer = containerRef.current;
      }
    };

    return (
      <div
        className={containerClass}
        ref={containerRef}
        onClick={setActivePlayer}
        onFocus={setActivePlayer}
      >
        <ReactPlayer
          url={videoUrl}
          controls={false}
          className="reactPlayer"
          style={styles.containerStyle}
          config={PlayerConfig}
          playing={playStatus}
          ref={getRef}
          onReady={onPlayerReady}
          onProgress={onProgressChange}
          muted={isMuted}
          volume={1}
          onDuration={onDuration}
          onPlay={onPlay}
          onPause={onPause}
          onEnded={onPause}
          onBuffer={onBuffer}
          onBufferEnd={onBufferEnd}
          pip={false}
        />
        <div
          className={classes.videoOverlay}
          onClick={() => playOrPauseVideo(!playStatus)}
        >
          {/* showPlayButton is given by user: whether he wants to show play button or not
            showCenterPlay is a hook that will toggle when the play/pause button is clicked
         */}
          {showPlayButton && showCenterPlay ? (
            <IconButton
              icon={
                playStatus == true ? (
                  <PauseFilled variant="on" />
                ) : (
                  <PlayFilled variant="on" />
                )
              }
              className={dsVersion === "2.0" ? classes.playPauseIcon : ""}
              variant="dark"
              size="medium"
            />
          ) : null}
          {isBufferProgress ? (
            <div className={classes.bufferLoader}>
              {" "}
              <Loading />{" "}
            </div>
          ) : null}
          {showControls ? (
            <div
              className={classes.videoControlsContainer}
              style={getControlsVisibility()}
            >
              <input
                type={"range"}
                className={classes.seekbar}
                onChange={onSeekbarChange}
                onClick={e => {
                  e?.stopPropagation();
                }}
                onMouseUp={e => {
                  e?.stopPropagation();
                }}
                min="0"
                max={totalDuration == 0 ? undefined : totalDuration}
                step={0.1}
                value={currentDuration}
                style={{
                  backgroundSize: `${
                    (currentDuration * 100) / Math.max(totalDuration, 1)
                  }% 100%`,
                }}
              />
              <div className={classes.videoControlsLeftSideContainer}>
                <IconButton
                  icon={
                    playStatus == true ? (
                      <PauseFilled variant="on" />
                    ) : (
                      <PlayFilled variant="on" />
                    )
                  }
                  variant="inline"
                  size="medium"
                  _legacy_variant={"inline"}
                  type="outlined"
                  style={styles.iconStyle}
                  onClick={togglePlayback}
                />
                <div className={classes.durationText}>{durationDetails}</div>
              </div>
              <div className={classes.videoControlsRightSideContainer}>
                <IconButton
                  icon={
                    isMuted == false ? (
                      <VolumeHighOutlined variant="on" />
                    ) : (
                      <VolumeLowOutlined variant="on" />
                    )
                  }
                  variant="inline"
                  size="medium"
                  _legacy_variant={"inline"}
                  type="outlined"
                  style={styles.iconStyle}
                  onClick={toggleMute}
                />
                <IconButton
                  icon={
                    isFullScreen ? (
                      <CollapseArrowsTwoOutlined variant="on" />
                    ) : (
                      <ExpandArrowsFourOutlined variant="on" />
                    )
                  }
                  variant="inline"
                  size="medium"
                  _legacy_variant={"inline"}
                  type="outlined"
                  style={styles.iconStyle}
                  onClick={toggleFullScreen}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
);

VideoPlayerV2.displayName = "VideoPlayer";

VideoPlayerV2.defaultProps = {
  autoPlay: false,
  showControls: true,
  showPlayButton: true,
  shouldFitVideoCover: false,
  useLazyVideoPlayer: false,
  isolateSpacebarControl: false,
};

export default VideoPlayerV2;
